import { CREATIVE_GRADIENT_STOPS, CREATIVE_SWATCHES } from '../../../../theme/tokens';
import { TEXT_SCALE, type CreativeDraft, type CreativeFormat, type TextLayer } from '../../custom-formats';

/**
 * Render a {@link CreativeDraft} to an off-screen `<canvas>` at the format's native resolution, so
 * it can be exported as a PNG. Hand-drawn (no dependencies): background (image / gradient / colour),
 * a legibility scrim, then each freely-positioned element (headline, subheadline, CTA pill, logo)
 * drawn about its own centre exactly where it sits on the live canvas. Throws if a cross-origin
 * background/logo taints the canvas (the caller offers a colour/gradient fallback then).
 */

const WHITE = CREATIVE_SWATCHES[0];
const NEAR_BLACK = CREATIVE_SWATCHES[1];

const gradientById = (id: string): { angle: number; stops: readonly string[] } =>
  CREATIVE_GRADIENT_STOPS.find((g) => g.id === id) ?? CREATIVE_GRADIENT_STOPS[0];

const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('image load failed'));
    img.src = src;
  });

const roundRect = (ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void => {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
};

const wrap = (ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] => {
  const out: string[] = [];
  for (const paragraph of text.split('\n')) {
    const words = paragraph.split(/\s+/).filter(Boolean);
    let line = '';
    for (const word of words) {
      const test = line ? `${line} ${word}` : word;
      if (ctx.measureText(test).width > maxWidth && line) {
        out.push(line);
        line = word;
      } else {
        line = test;
      }
    }
    out.push(line);
  }
  return out.length ? out : [''];
};

/** Draw a text layer about its own centre (`layer.pos`), honouring size scale + rotation, wrapping
 *  + aligning like the live canvas. Drawn in a translated/rotated frame whose origin IS the centre. */
const drawText = (
  ctx: CanvasRenderingContext2D,
  layer: TextLayer,
  family: string,
  weight: number,
  lineFactor: number,
  w: number,
  h: number,
  minSide: number,
): void => {
  if (!layer.text) return;
  // Font = base × scale; wrap at the element's own box width; align within that box (matches the DOM).
  const fontPx = TEXT_SCALE[layer.size] * layer.scale * minSide;
  ctx.font = `${weight} ${fontPx}px ${family}`;
  const boxW = layer.width * w;
  const lines = wrap(ctx, layer.text, boxW);
  const lineHeight = fontPx * lineFactor;
  const blockH = lines.length * lineHeight;

  ctx.save();
  ctx.translate(layer.pos.x * w, layer.pos.y * h);
  if (layer.rot) ctx.rotate((layer.rot * Math.PI) / 180);
  ctx.fillStyle = layer.color;
  ctx.textBaseline = 'top';
  ctx.textAlign = layer.align === 'center' ? 'center' : layer.align === 'right' ? 'right' : 'left';
  const anchorX = layer.align === 'center' ? 0 : layer.align === 'right' ? boxW / 2 : -boxW / 2;
  let y = -blockH / 2;
  for (const line of lines) {
    ctx.fillText(line, anchorX, y);
    y += lineHeight;
  }
  ctx.restore();
};

export async function renderCreativeToCanvas(
  draft: CreativeDraft,
  format: CreativeFormat,
  logoUrl: string | undefined,
): Promise<HTMLCanvasElement> {
  const { w, h } = format;
  const minSide = Math.min(w, h);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('no 2d context');

  // ── Background ──
  const bg = draft.background;
  if (bg.kind === 'image' && bg.imageUrl) {
    const img = await loadImage(bg.imageUrl);
    const scale = Math.max(w / img.width, h / img.height);
    const dw = img.width * scale;
    const dh = img.height * scale;
    // Honour the pan offset (−0.5..0.5), matching the DOM's background-position.
    ctx.drawImage(img, -(dw - w) * (0.5 + bg.offset.x), -(dh - h) * (0.5 + bg.offset.y), dw, dh);
    if (bg.scrim > 0) {
      ctx.globalAlpha = bg.scrim;
      ctx.fillStyle = NEAR_BLACK;
      ctx.fillRect(0, 0, w, h);
      ctx.globalAlpha = 1;
    }
  } else if (bg.kind === 'gradient') {
    const g = gradientById(bg.value);
    const a = (g.angle * Math.PI) / 180;
    const cx = w / 2;
    const cy = h / 2;
    const len = Math.abs(w * Math.sin(a)) + Math.abs(h * Math.cos(a));
    const dx = (Math.sin(a) * len) / 2;
    const dy = (-Math.cos(a) * len) / 2;
    const grad = ctx.createLinearGradient(cx - dx, cy - dy, cx + dx, cy + dy);
    g.stops.forEach((s, i) => grad.addColorStop(g.stops.length === 1 ? 0 : i / (g.stops.length - 1), s));
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, w, h);
  } else {
    ctx.fillStyle = bg.value;
    ctx.fillRect(0, 0, w, h);
  }

  // Match the preview's webfont before measuring/drawing text.
  try {
    await document.fonts?.ready;
  } catch {
    /* fonts API unavailable — fall back to system metrics */
  }

  drawText(ctx, draft.headline, 'Satoshi, -apple-system, sans-serif', 800, 1.05, w, h, minSide);
  drawText(ctx, draft.subheadline, "-apple-system, 'Segoe UI', sans-serif", 500, 1.18, w, h, minSide);

  // ── CTA pill (an explicit box centred on its position, rotated; font from `scale`) ──
  if (draft.cta.show && draft.cta.text) {
    const boxW = draft.cta.width * w;
    const boxH = draft.cta.height * h;
    const fs = 0.036 * draft.cta.scale * minSide;
    ctx.save();
    ctx.translate(draft.cta.pos.x * w, draft.cta.pos.y * h);
    if (draft.cta.rot) ctx.rotate((draft.cta.rot * Math.PI) / 180);
    ctx.fillStyle = draft.cta.color;
    roundRect(ctx, -boxW / 2, -boxH / 2, boxW, boxH, boxH / 2);
    ctx.fill();
    ctx.fillStyle = WHITE;
    ctx.font = `700 ${fs}px -apple-system, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(draft.cta.text, 0, fs * 0.05);
    ctx.restore();
    ctx.textBaseline = 'top';
  }

  // ── Logo (about its own centre, rotated) ──
  if (draft.logo.show && logoUrl) {
    try {
      const img = await loadImage(logoUrl);
      const lh = draft.logo.scale * minSide;
      const lw = Math.min(img.width * (lh / img.height), 0.5 * w);
      ctx.save();
      ctx.translate(draft.logo.pos.x * w, draft.logo.pos.y * h);
      if (draft.logo.rot) ctx.rotate((draft.logo.rot * Math.PI) / 180);
      ctx.drawImage(img, -lw / 2, -lh / 2, lw, lh);
      ctx.restore();
    } catch {
      /* logo is optional — skip if it fails to load */
    }
  }

  // ── Extras (added logos / stickers), in draw order ──
  for (const ex of draft.extras) {
    try {
      const img = await loadImage(ex.src);
      const eh = ex.scale * minSide;
      const ew = Math.min(img.width * (eh / img.height), 0.9 * w);
      ctx.save();
      ctx.translate(ex.pos.x * w, ex.pos.y * h);
      if (ex.rot) ctx.rotate((ex.rot * Math.PI) / 180);
      ctx.drawImage(img, -ew / 2, -eh / 2, ew, eh);
      ctx.restore();
    } catch {
      /* an extra that fails to load is skipped */
    }
  }

  return canvas;
}
